import { forwardRef, useImperativeHandle, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { NumberInput, Select, TextInput, TextareaInput } from 'components/FormControl';

import { yupResolver } from '@hookform/resolvers/yup';
import { TOAST } from 'constant';
import { usefulSolutionSchema } from 'features/ScientificProfile/validation';
import { toast } from 'layout';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';

const formOptions = { resolver: yupResolver(usefulSolutionSchema) };

const UsefulSolutionDialog = forwardRef((props, ref) => {
  const { t } = useTranslation();

  const [usefulSolutionControl, setUsefulSolutionControl] = useState();
  const [visible, setVisible] = useState(false);

  const contributorRoles = [
    { value: 'author', name: t('contributorRole.author') },
    { value: 'coAuthor', name: t('contributorRole.coAuthor') },
  ];

  const {
    control,
    watch,
    reset,
    trigger,
    formState: { errors, dirtyFields },
  } = useForm({ ...formOptions, mode: 'onChange' });
  // #region Event

  useImperativeHandle(
    ref,
    () => ({
      open: (_usefulSolutionControl) => {
        setUsefulSolutionControl(_usefulSolutionControl);
        setVisible(true);
        if (_usefulSolutionControl.type === 'INSERT') {
          reset({});
        } else if (_usefulSolutionControl.type === 'UPDATE') {
          reset(_usefulSolutionControl.value);
        }
      },
    }),
    []
  );

  const handleAddUsefulSolution = async () => {
    const isValidTrigger = await trigger();
    if (!isValidTrigger) {
      toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
      return;
    }

    const data = watch();

    const { insert, update, index, type } = usefulSolutionControl;

    if (type === 'INSERT' && insert) {
      insert(index, data);
    } else if (type === 'UPDATE' && update) {
      update(index, data);
    }
    reset({});
    setVisible(false);
  };
  // #endregion Event

  return (
    <Dialog
      header={
        usefulSolutionControl?.type === 'INSERT'
          ? t('scientificProfile.otherInfo.usefulSolution.add')
          : t('scientificProfile.otherInfo.usefulSolution.update')
      }
      visible={visible}
      onHide={() => {
        setVisible(false);
      }}
      draggable={false}
      className="w-full sm:w-7 lg:w-6"
    >
      <div className="grid p-fluid">
        <div className="col-12">
          <TextareaInput
            label={t('scientificProfile.otherInfo.usefulSolution.name')}
            name="name"
            control={control}
            errors={errors}
            isRequired
          />
        </div>

        <div className="col-6">
          <TextInput
            label={t('scientificProfile.otherInfo.usefulSolution.projectCode')}
            name="projectCode"
            control={control}
            errors={errors}
            isRequired
          />
        </div>
        <div className="col-6">
          <TextInput
            label={t('scientificProfile.otherInfo.usefulSolution.number')}
            name="number"
            control={control}
            errors={errors}
            isRequired
          />
        </div>

        <div className="col-12">
          <TextareaInput
            label={t('scientificProfile.otherInfo.usefulSolution.placeOfIssue')}
            name="placeOfIssue"
            control={control}
            errors={errors}
            isRequired
          />
        </div>

        <div className="col-6">
          <NumberInput
            label={t('scientificProfile.otherInfo.usefulSolution.yearOfIssue')}
            name="yearOfIssue"
            control={control}
            errors={errors}
            min={0}
            isRequired
          />
        </div>
        <div className="col-6">
          <Select
            label={t('scientificProfile.otherInfo.usefulSolution.authorOrCoauthor')}
            name="contributorRole"
            options={contributorRoles}
            control={control}
            errors={errors}
            inputField="value"
            isRequired
          />
        </div>
      </div>

      <div className="flex flex-wrap justify-content-end mt-4">
        <Button
          label={t('formLayout.action.cancel')}
          severity="danger"
          onClick={() => setVisible(false)}
          className="mr-1 w-8rem"
        />
        <Button
          label={
            usefulSolutionControl?.type === 'INSERT'
              ? t('formLayout.action.add')
              : t('formLayout.action.update')
          }
          severity="info"
          type="submit"
          className="w-8rem"
          onClick={handleAddUsefulSolution}
          disabled={!Object.keys(dirtyFields).length}
        />
      </div>
    </Dialog>
  );
});

UsefulSolutionDialog.propTypes = {};

UsefulSolutionDialog.defaultProps = {};

export default UsefulSolutionDialog;
